import { Icon } from 'common/icon';
import { Button } from 'common/new-button';
import { Routes } from 'constants/routes';
import { IQuestion } from 'constants/types';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';
import styles from './styles.module.css';
import { getItemIdentifier } from 'utils/slug';

interface ProfileCardProps {
  name: string;
  imageUrl: string;
  description: string;
  questions?: IQuestion[];
}

const socialIcons = [
  {
    name: 'Email',
    icon: <Icon.EmailSecond />,
    link: 'mailto:michael.carter@rtpcheck.com',
  },
  {
    name: 'Telegram',
    icon: <Icon.TelegramSecond height="16" width="16" fill="white" />,
    link: 'https://t.me/rtpcheck',
  },
  // {
  //   id: 1,
  //   name: 'WhatsApp',
  //   icon: <Icon.WhatsAppThird height="16" width="16" />,
  // },
  // { id: 3, name: 'Skype', icon: <Icon.SkypeSecond /> },
];

interface CommunitySliderProps {
  questions?: IQuestion[];
}

const CommunitySlider = ({ questions }: CommunitySliderProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: false,
    slidesToScroll: 1,
    loop: true,
  });
  const [autoScrollDirection, setAutoScrollDirection] = useState<
    'forward' | 'backward'
  >('forward');
  const [isPaused, setIsPaused] = useState(false);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi || !questions || questions.length <= 1) return;

    const handleScroll = () => {
      // Detect when we're at the end or start to change direction
      if (
        emblaApi.canScrollNext() === false &&
        autoScrollDirection === 'forward'
      ) {
        setAutoScrollDirection('backward');
      } else if (
        emblaApi.canScrollPrev() === false &&
        autoScrollDirection === 'backward'
      ) {
        setAutoScrollDirection('forward');
      }
    };

    emblaApi.on('settle', handleScroll);

    // Auto-scroll interval
    const autoScrollInterval = setInterval(() => {
      if (!isPaused) {
        if (autoScrollDirection === 'forward') {
          scrollNext();
        } else {
          scrollPrev();
        }
      }
    }, 3000); // Scroll every 3 seconds

    return () => {
      emblaApi.off('settle', handleScroll);
      clearInterval(autoScrollInterval);
    };
  }, [
    emblaApi,
    scrollNext,
    scrollPrev,
    autoScrollDirection,
    isPaused,
    questions,
  ]);

  // Pause scrolling when hovering
  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);

  return (
    <div
      className={styles.sliderContainer}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.embla}>
        <div className={styles.embla__viewport} ref={emblaRef}>
          <div className={styles.embla__container}>
            {questions?.map((question) => (
              <Link
                href={Routes.question(getItemIdentifier(question))}
                className={styles.embla__slide}
                key={question._id}
              >
                <div className={styles.memberCard}>
                  <div className={styles.memberAvatarWrapper}>
                    <Image
                      src={question.user?.imageUrl || '/images/avatar.png'}
                      alt={question.user?.username || 'User Avatar'}
                      width={50}
                      height={50}
                      className={styles.memberAvatar}
                    />
                  </div>
                  <div className={styles.memberInfo}>
                    <span className={styles.memberName}>
                      {question.user?.username || 'Unknown User'}
                    </span>
                    <span className={styles.memberMessage}>
                      {question.message?.length > 30
                        ? `${question.message.slice(0, 30)}...`
                        : question.message}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const ProfileCard = ({
  name,
  imageUrl,
  description,
  questions,
}: ProfileCardProps) => {
  return (
    <div className={styles.container}>
      <div className={styles.leftColumn}>
        <div className={styles.profileSection}>
          <div className={styles.avatarContainer}>
            <div className={styles.avatarFrame}>
              <Image
                src={imageUrl}
                alt={name}
                width={130}
                height={130}
                className={styles.avatar}
              />
            </div>
          </div>
          <div className={styles.profileInfo}>
            <h3 className={styles.name}>{name}</h3>
            <p className={styles.bio}>{description}</p>
          </div>
        </div>
        <div className={styles.socialLinks}>
          {socialIcons.map((social, index) => (
            <Link
              target="_blank"
              key={index}
              className={styles.socialButton}
              href={social.link}
            >
              <span className={styles.socialIcon}>{social.icon}</span>
              <span>{social.name}</span>
            </Link>
          ))}
        </div>
      </div>

      <div className={styles.rightColumn}>
        <div className={styles.communityPrompt}>
          <Icon.MessageFourth width="20" height="20" />
          <span>or ask in our community</span>
        </div>

        <CommunitySlider questions={questions} />

        <Link href={Routes.questions} className={styles.askQuestionContainer}>
          <Button
            text={'Ask a question'}
            icon={<Icon.MessageThird width="16" height="16" />}
            className={styles.askQuestionButton}
          />
        </Link>
      </div>
    </div>
  );
};

interface ProfilesProps {
  data: ProfileCardProps[];
  questions: IQuestion[];
}

export const Profiles = ({ data, questions }: ProfilesProps) => {
  return (
    <div className={styles.profilesContainer}>
      {data.map((profile) => (
        <ProfileCard
          questions={questions}
          key={profile.name}
          name={profile.name}
          imageUrl={profile.imageUrl}
          description={profile.description}
        />
      ))}
    </div>
  );
};
